import React, { ReactElement, ReactNode, cloneElement, useRef, useState } from 'react';

import { DownOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Popover } from 'antd';
import { colors } from 'theme/colors';

import { Button } from 'components/Button';
import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { Counter } from 'components/Counter';
import { Tooltip } from 'components/Tooltip';

import { arrIsEqual } from 'utils/array';

import { SelectableItemList } from './SelectableItemList';
import { MultiSelectOption, UseAsyncMultiSelectProps } from './types';

export interface AsyncMultiSelectProps {
  selectProps: UseAsyncMultiSelectProps | ((isOpen: boolean) => UseAsyncMultiSelectProps);
  selectedValues: string[];
  onSelectedValuesChange: (values: string[]) => void;
  label?: string;
  icon?: ReactElement;
  mode?: 'multiple' | 'single';
  queryPlaceholder?: string;
  triggerButton?: ReactNode;
  containerClassName?: string;
  popoverClassName?: string;
  disableValuesTooltip?: boolean;
  orderable?: boolean;
  showClearButtonOnSingleMode?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export function AsyncMultiSelect({
  label,
  icon,
  selectProps,
  mode = 'multiple',
  selectedValues,
  onSelectedValuesChange,
  queryPlaceholder,
  triggerButton,
  containerClassName,
  popoverClassName,
  disableValuesTooltip,
  orderable,
  showClearButtonOnSingleMode = false,
  onOpenChange,
}: AsyncMultiSelectProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [curSelectedValues, setCurSelectedValues] = useState<string[]>(selectedValues);
  const optionsCache = useRef(new Map<string, MultiSelectOption>());

  const selectPropsObj = typeof selectProps === 'function' ? selectProps(isOpen) : selectProps;

  // cache all seen options so selected items remain visible even if they fall outside the paginated window
  selectPropsObj.options.forEach((o) => optionsCache.current.set(o.value, o));
  const resolvedSelectedOptions = selectedValues
    .filter((v) => !selectPropsObj.options.some((o) => o.value === v))
    .map((v) => optionsCache.current.get(v))
    .filter((o): o is MultiSelectOption => o !== undefined);
  const enrichedSelectProps =
    resolvedSelectedOptions.length > 0 ? { ...selectPropsObj, resolvedSelectedOptions } : selectPropsObj;

  const applyValues = (values: string[]) => {
    onSelectedValuesChange(values);
    setIsOpen(false);
    selectPropsObj.setSearchQuery?.('');
  };

  const handleOpenChange = (newOpenState: boolean) => {
    setIsOpen(newOpenState);
    if (newOpenState) setCurSelectedValues(selectedValues);
    onOpenChange?.(newOpenState);
  };

  return (
    <ConditionalWrapper
      condition={!disableValuesTooltip && mode === 'multiple' && selectedValues.length > 0}
      wrapper={(children) => (
        <Tooltip title={selectedValues.join('\n')} overlayInnerStyle={{ whiteSpace: 'pre-line' }}>
          {children}
        </Tooltip>
      )}
    >
      <Popover
        placement="bottomLeft"
        trigger="click"
        visible={isOpen}
        onVisibleChange={handleOpenChange}
        autoAdjustOverflow
        content={
          <>
            <SelectableItemList
              selectProps={enrichedSelectProps}
              selectedValues={curSelectedValues}
              pinnedValues={selectedValues}
              onSelectedValuesChange={(values) =>
                mode === 'single' ? applyValues(values) : setCurSelectedValues(values)
              }
              orderable={orderable}
              mode={mode}
              queryPlaceholder={queryPlaceholder}
              popoverClassName={popoverClassName}
            />

            <div
              className={css`
                display: flex;
                align-items: center;
                gap: 8px;
                justify-content: flex-end;
              `}
            >
              {(mode === 'multiple' || showClearButtonOnSingleMode) && (
                <Button
                  size="small"
                  onClick={() => {
                    applyValues([]);
                  }}
                >
                  Clear
                </Button>
              )}
              {mode === 'multiple' ? (
                <Button
                  onClick={() => applyValues(curSelectedValues)}
                  size="small"
                  type="primary"
                  disabled={arrIsEqual(curSelectedValues, selectedValues)}
                >
                  Apply
                </Button>
              ) : null}
            </div>
          </>
        }
      >
        {/* Ensure clicks propogate to the popover with a custom TriggerButton */}
        {triggerButton ? (
          <div onClick={() => handleOpenChange(!isOpen)}>{triggerButton}</div>
        ) : (
          <Button
            size="small"
            className={`
              ${containerClassName}
              ${css`
                &.ant-btn {
                  border-color: ${colors.neutral[300]};

                  &:focus,
                  &:hover {
                    border-color: ${colors.primary[500]};
                    color: inherit;
                  }
                }
              `}
            `}
          >
            <div
              className={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 6px;
              `}
            >
              <div
                className={css`
                  display: flex;
                  align-items: center;
                  gap: 6px;
                `}
              >
                {icon && cloneElement(icon, { style: { color: colors.neutral[500] } })}
                {label}
                {mode === 'single' ? (
                  <>
                    {label && selectedValues.length > 0 ? `:` : ``}
                    <span
                      className={css`
                        color: ${label ? colors.primary[500] : 'inherit'};
                      `}
                    >
                      {selectPropsObj.options.find((option) => option.value === selectedValues[0])?.label ??
                        selectedValues[0]}
                    </span>
                  </>
                ) : null}
                {mode === 'multiple' ? (
                  <Counter value={selectedValues.length} selected={selectedValues.length > 0} variant="primary" />
                ) : null}
              </div>
              <DownOutlined style={{ color: colors.neutral[400], fontSize: '12px' }} />
            </div>
          </Button>
        )}
      </Popover>
    </ConditionalWrapper>
  );
}

export function filterMultiSelectOptions(
  multiSelectProps: UseAsyncMultiSelectProps,
  values?: string[],
): UseAsyncMultiSelectProps {
  if (!values) return multiSelectProps;
  return { ...multiSelectProps, options: multiSelectProps.options.filter((option) => values.includes(option.value)) };
}
